import { useNavigate } from "react-router-dom";

import { ToastPosition } from "@atoms/Toast/constants/toast.constants";
import { showToast } from "@atoms/Toast/Toast";
import { ROUTE_PATHS } from "@routes/routePath";

export const useToastDBSaved = (position: ToastPosition = "bottomLeft") => {
  const navigate = useNavigate();
  const currentPath = window.location.pathname;
  return (pressId: string) => {
    showToast({
      text: "DB Save completed!",
      backgroundColor: "sky",
      position: currentPath.includes("fill-in-table") ? "topCenter" : position,
      icon: "save",
      customButton: [
        {
          name: "View",
          action: () => {
            navigate({
              pathname: ROUTE_PATHS.fillInTable,
              search: `?id=${pressId}`,
            });
          },
        },
      ],
    });
  };
};

export const useToastAiEntryCompleted = (
  position: ToastPosition = "bottomLeft",
) => {
  const navigate = useNavigate();
  const currentPath = window.location.pathname;
  let destination: string = ROUTE_PATHS.insertImage;

  if (window.location.pathname === ROUTE_PATHS.editArticle)
    destination = ROUTE_PATHS.editArticle;

  return (pressId: number) => {
    showToast({
      backgroundColor: "green",
      text: "AI Entry completed!",
      icon: "edit",
      position: currentPath.includes("fill-in-table") ? "topCenter" : position,
      customButton: [
        {
          name: "View",
          action: () =>
            navigate({
              pathname: destination,
              search: `?id=${pressId}`,
            }),
        },
      ],
    });
  };
};

export const toastArticlePublish = (
  urlTitle: string,
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: "Article Publish completed!",
    backgroundColor: "blue",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
    icon: "edit",
    customButton: [
      {
        name: "View",
        action: () => {
          window.open(`${import.meta.env.VITE_PUBLISH_URL}/${urlTitle}`);
        },
      },
    ],
  });
};

export const toastDraftSave = (position: ToastPosition = "bottomLeft") => {
  const currentPath = window.location.pathname;

  showToast({
    text: "Draft Save completed!",
    backgroundColor: "blueGreen",
    icon: "save",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastSetDBSaved = (position: ToastPosition = "bottomLeft") => {
  const currentPath = window.location.pathname;

  showToast({
    text: "Set Status As DB Save completed!",
    backgroundColor: "blueGreen",
    icon: "save",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastFailUpdatedEditor = (
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: "Fail to update editor!",
    backgroundColor: "red",
    icon: "error",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastFail = (
  message = "Unknown Error!",
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: message,
    backgroundColor: "red",
    icon: "error",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastSuccess = (
  message = "Success!",
  backgroundColor: "green" | "blueGreen" = "green",
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: message,
    backgroundColor,
    icon: "edit",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastWaitForAI = (
  count: number,
  total: number,
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: `Waiting for AI... (${count} / ${total})`,
    backgroundColor: "orange",
    icon: "edit",
    autoClose: 2000,
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastFailedCreateAiImage = (
  draftArticleId: number,
  moveToViewImage: () => void,
  retryCreateAiImage: () => void,
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: `Image generation failed\n(PRESS ID: #${draftArticleId})`,
    backgroundColor: "hotPink",
    icon: "alertTriangle",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
    customButton: [
      {
        name: "View",
        action: moveToViewImage,
      },
      {
        name: "Retry",
        action: retryCreateAiImage,
      },
    ],
    autoClose: 5000,
  });
};

export const toastSuccessCreateAiImage = (
  draftArticleId: number,
  moveToViewImage: () => void,
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: `Image generation complete\n(PRESS ID: #${draftArticleId})`,
    backgroundColor: "green",
    icon: "aiComplete",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
    customButton: [
      {
        name: "View",
        action: moveToViewImage,
      },
    ],
    autoClose: 5000,
  });
};

export const toastCreateAiImageSuccess = (
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: "Start generating AI images.",
    backgroundColor: "green",
    icon: "aiComplete",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastCreateAiImageFailed = (
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: "Failed to generate AI images.",
    backgroundColor: "hotPink",
    icon: "alertTriangle",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
  });
};

export const toastSuccessCopyPress = (
  draftArticleId: number,
  moveToPage: () => void,
  position: ToastPosition = "bottomLeft",
) => {
  const currentPath = window.location.pathname;

  showToast({
    text: `Press Duplication Complete\nPRESS ID: #${draftArticleId}`,
    backgroundColor: "green",
    icon: "copy",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
    customButton: [
      {
        name: "View",
        action: moveToPage,
      },
    ],
    autoClose: 5000,
  });
};

export const toastFailCopyPress = (position: ToastPosition = "bottomLeft") => {
  const currentPath = window.location.pathname;

  showToast({
    text: `Press Duplication Failed`,
    backgroundColor: "hotPink",
    icon: "alertTriangle",
    position: currentPath.includes("fill-in-table") ? "topCenter" : position,
    autoClose: 5000,
  });
};
